import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    this.#variantUpdateSectionEl = closestSection;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);

    // PDP: selling plan selection may be injected by apps after initial render.
    // We listen for changes and update the displayed price accordingly.
    this.#attachSellingPlanListeners();
    this.#syncSellingPlanPrice();
  }

  disconnectedCallback() {
    this.#variantUpdateSectionEl?.removeEventListener?.(ThemeEvents.variantUpdate, this.updatePrice);
    this.#variantUpdateSectionEl = null;

    this.#sellingPlanObserver?.disconnect();
    this.#sellingPlanObserver = null;

    // Clean up element-bound listeners (document listeners are cleaned up via AbortController below).
    this.#sellingPlanFormEl?.removeEventListener?.('change', this.#onFormChange);
    this.#sellingPlanSectionEl?.removeEventListener?.('change', this.#onFormChange);
    this.#sellingPlanSectionEl?.removeEventListener?.('input', this.#onFormChange);
    this.#sellingPlanFormEl = null;
    this.#sellingPlanSectionEl = null;

    this.#sellingPlanAbort?.abort();
    this.#sellingPlanAbort = null;
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId && event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
    }

    // Re-apply selling plan price after the variant update re-renders the OTP price.
    this.#rechargeSyncAttempts = 0;
    this.#syncSellingPlanPrice();
  };

  /** @type {MutationObserver | null} */
  #sellingPlanObserver = null;
  /** @type {AbortController | null} */
  #sellingPlanAbort = null;
  /** @type {HTMLElement | null} */
  #sellingPlanSectionEl = null;
  /** @type {HTMLElement | null} */
  #sellingPlanFormEl = null;
  /** @type {HTMLElement | null} */
  #variantUpdateSectionEl = null;
  /** @type {number} */
  #rechargeSyncAttempts = 0;

  #attachSellingPlanListeners() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;

    const form = this.#getClosestProductForm();
    this.#sellingPlanSectionEl = closestSection;
    this.#sellingPlanFormEl = form instanceof HTMLElement ? form : null;

    // Listen for native changes (if input exists now)
    if (form) {
      form.addEventListener('change', this.#onFormChange, { passive: true });
    }

    // Also listen at section level (Recharge may render selling_plan inputs outside the add-to-cart form)
    closestSection.addEventListener('change', this.#onFormChange, { passive: true });
    closestSection.addEventListener('input', this.#onFormChange, { passive: true });

    // As a last resort, listen at document level in capture phase (helps if events originate in shadow DOM)
    this.#sellingPlanAbort?.abort();
    this.#sellingPlanAbort = new AbortController();
    document.addEventListener('change', this.#onFormChange, {
      passive: true,
      capture: true,
      signal: this.#sellingPlanAbort.signal,
    });
    document.addEventListener('input', this.#onFormChange, {
      passive: true,
      capture: true,
      signal: this.#sellingPlanAbort.signal,
    });
    document.addEventListener('click', this.#onFormChange, {
      passive: true,
      capture: true,
      signal: this.#sellingPlanAbort.signal,
    });

    // Also watch for late-injected selling plan inputs (apps)
    if (this.#sellingPlanObserver) return;
    this.#sellingPlanObserver = new MutationObserver(() => {
      // If a selling plan input was injected, sync immediately.
      this.#syncSellingPlanPrice();
    });
    // Prefer observing the form if it exists, otherwise observe the section.
    this.#sellingPlanObserver.observe(form || closestSection, { childList: true, subtree: true });
  }

  /** @param {Event} event */
  #onFormChange = (event) => {
    // If the user interacted within the Recharge widget (labels/divs inside shadow DOM, etc),
    // we should resync even if the event target isn't an <input>.
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const inRechargeWidget = path.some((node) => {
      if (!(node instanceof Element)) return false;
      return node.classList?.contains('recharge-subscription-widget') || node.tagName?.toLowerCase() === 'recharge-subscription-widget';
    });

    const target = event.target;
    if (target instanceof HTMLInputElement || target instanceof HTMLSelectElement) {
      // Recharge uses purchaseOption radios; variants often update the URL param.
      if (target.name === 'selling_plan' || target.name === 'id' || target.name === 'purchaseOption') {
        this.#rechargeSyncAttempts = 0;
        this.#syncSellingPlanPrice();
        return;
      }
    }

    if (inRechargeWidget) {
      this.#rechargeSyncAttempts = 0;
      this.#syncSellingPlanPrice();
    }
  };

  #getClosestProductForm() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return null;

    const productId = this.dataset.productId;
    if (!productId) return null;

    // Prefer theme product-form-component, but fall back to the form itself.
    return (
      closestSection.querySelector(`product-form-component[data-product-id="${productId}"]`) ||
      closestSection.querySelector(`form[data-type="add-to-cart-form"]`)
    );
  }

  #getSellingPlanPriceMap() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return null;

    const productId = this.dataset.productId;
    if (!productId) return null;

    const script = closestSection.querySelector(
      `script[type="application/json"][data-selling-plan-price-map][data-product-id="${productId}"]`
    );
    if (!(script instanceof HTMLScriptElement)) return null;

    try {
      return JSON.parse(script.textContent || '');
    } catch {
      return null;
    }
  }

  #getCurrentVariantId() {
    const formRoot = this.#getClosestProductForm();
    if (formRoot) {
      const form = formRoot.matches('form') ? formRoot : formRoot.querySelector('form[data-type="add-to-cart-form"]');
      const input = form?.querySelector('input[name="id"]');
      if (input instanceof HTMLInputElement && input.value) return input.value;
    }

    // Fallback: read from URL (works even if product form input is not present/updated yet).
    try {
      const url = new URL(window.location.href);
      return url.searchParams.get('variant');
    } catch {
      return null;
    }
  }

  #getSelectedSellingPlanId() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return null;

    const formRoot = this.#getClosestProductForm();
    const form = formRoot?.matches?.('form')
      ? formRoot
      : formRoot?.querySelector?.('form[data-type="add-to-cart-form"]') || null;

    // Search order:
    // 1) Inside the actual add-to-cart form (ideal)
    // 2) Anywhere in the PDP section (Recharge app blocks often live outside the form)
    // 3) As a last fallback, anywhere in the document
    const rootsToSearch = [form, closestSection, document].filter(Boolean);

    for (const root of rootsToSearch) {
      // Radio / hidden / select are all possible depending on app implementation.
      const checkedInput = root.querySelector?.('input[name="selling_plan"]:checked');
      if (checkedInput instanceof HTMLInputElement) return checkedInput.value || null;

      const anyInput = root.querySelector?.('input[name="selling_plan"]');
      if (anyInput instanceof HTMLInputElement) return anyInput.value || null;

      const select = root.querySelector?.('select[name="selling_plan"]');
      if (select instanceof HTMLSelectElement) return select.value || null;
    }

    // Recharge widget v2: the selling plan selection is often represented by the widget's own radios
    // rendered inside a (potential) shadowRoot. Try to infer a selling plan id from the checked
    // frequency option within the widget.
    const rechargeRoots = this.#getRechargeSearchRoots();

    if (rechargeRoots.length) {
      const checkedRadios = rechargeRoots.flatMap((root) =>
        Array.from(root.querySelectorAll?.('input[type="radio"]:checked') || [])
      );
      const checkedSelects = rechargeRoots.flatMap((root) => Array.from(root.querySelectorAll?.('select') || []));

      // If "One-time" is selected, treat as no selling plan.
      for (const el of checkedRadios) {
        const labelText = el.closest('label')?.textContent?.toLowerCase() || '';
        const aria = el.getAttribute('aria-label')?.toLowerCase() || '';
        if (labelText.includes('one-time') || aria.includes('one-time')) {
          return null;
        }
      }

      // Prefer a numeric value that looks like a Shopify selling_plan id.
      for (const el of checkedRadios) {
        const value = (el instanceof HTMLInputElement && el.value) || '';
        if (/^\d{6,}$/.test(value)) return value;
      }

      // Some widgets use a select dropdown for frequency.
      for (const el of checkedSelects) {
        if (!(el instanceof HTMLSelectElement)) continue;
        const value = el.value || '';
        if (/^\d{6,}$/.test(value)) return value;
      }
    }

    return null;
  }

  /**
   * Formats money based on a Shopify money format string and currency code.
   * Replicates Shopify's Liquid money placeholder behavior (subset mirrored from facet-status-component).
   * @param {number} cents
   * @param {string} moneyFormat
   * @param {string} currency
   */
  #formatMoney(cents, moneyFormat, currency) {
    const DEFAULT_CURRENCY_DECIMALS = 2;
    const CURRENCY_DECIMALS = {
      BHD: 3,
      BIF: 0,
      BYR: 0,
      CLF: 4,
      CLP: 0,
      DJF: 0,
      GNF: 0,
      IQD: 3,
      ISK: 0,
      JOD: 3,
      JPY: 0,
      KMF: 0,
      KRW: 0,
      KWD: 3,
      LYD: 3,
      MRO: 5,
      OMR: 3,
      PYG: 0,
      RWF: 0,
      TND: 3,
      UGX: 0,
      UYI: 0,
      UYW: 4,
      VND: 0,
      VUV: 0,
      XAF: 0,
      XAG: 0,
      XAU: 0,
      XBA: 0,
      XBB: 0,
      XBC: 0,
      XBD: 0,
      XDR: 0,
      XOF: 0,
      XPD: 0,
      XPF: 0,
      XPT: 0,
      XSU: 0,
      XTS: 0,
      XUA: 0,
    };

    const upperCurrency = (currency || '').toUpperCase();
    const template = moneyFormat || '{{amount}}';

    const formatCents = (moneyValue, thousandsSeparator, decimalSeparator, precision) => {
      // Shopify money values are in minor units for the currency (e.g. JPY has 0 decimals, KWD has 3).
      // So the divisor is 10^precision.
      const divisor = Math.pow(10, precision);
      const amount = (moneyValue / divisor).toFixed(precision);
      const [whole, fraction] = amount.split('.');

      const wholeWithSeparators = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
      if (precision === 0) return wholeWithSeparators;
      return `${wholeWithSeparators}${decimalSeparator}${fraction}`;
    };

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return upperCurrency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[upperCurrency] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // defaults
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      } else {
        // Unknown placeholder, return empty string to avoid raw tokens.
        return '';
      }

      return formatCents(cents, thousandsSeparator, decimalSeparator, precision);
    });
  }

  #syncSellingPlanPrice() {
    const variantId = this.#getCurrentVariantId();
    if (!variantId) return;

    const purchaseState = this.#getRechargePurchaseState();
    const sellingPlanId = this.#getSelectedSellingPlanId();
    const priceContainer = this.querySelector('[ref="priceContainer"]');
    const priceEl = priceContainer?.querySelector('.price');
    const hasPriceSpan = priceEl instanceof HTMLElement;
    const hasPriceContainer = priceContainer instanceof HTMLElement;
    if (!hasPriceSpan && !hasPriceContainer) return;

    // Prefer the server-rendered map when available (cents + correct money_format),
    // but do not require it: Recharge exposes formatted prices we can use as a fallback.
    const priceMap = this.#getSellingPlanPriceMap();
    const variantEntry = priceMap?.variants?.[variantId] || null;
    const moneyFormat = priceMap?.money_format || '{{amount}}';
    const currency = priceMap?.currency || window.SubscriptionWidgetConfig?.shop?.currency || '';
    const shouldShowCurrencyCode = !!priceMap?.money_format?.includes?.('{{currency}}');

    /** @param {number} cents */
    const applyFormattedPrice = (cents) => {
      const formatted = this.#formatMoney(cents, moneyFormat, currency);
      if (hasPriceSpan) {
        priceEl.innerHTML = formatted;
      } else if (hasPriceContainer) {
        // Fallback for themes that render price as plain text inside priceContainer (no `.price` span).
        priceContainer.textContent = formatted;
      }
    };

    /** @param {string} formatted */
    const applyFormattedString = (formatted) => {
      const value = shouldShowCurrencyCode && currency ? `${formatted} ${currency}` : formatted;
      if (hasPriceSpan) {
        priceEl.textContent = value;
      } else if (hasPriceContainer) {
        priceContainer.textContent = value;
      }
    };

    const rechargeFormatted = window.SubscriptionWidgetConfig?.formattedPrices?.[variantId];

    // Recharge loads async; if widget exists but config isn't ready yet, retry a few times.
    if (purchaseState && !rechargeFormatted && this.#rechargeSyncAttempts < 15) {
      this.#rechargeSyncAttempts += 1;
      setTimeout(() => this.#syncSellingPlanPrice(), 150);
      return;
    }

    // Recharge widget: if "One-time" is selected, force OTP.
    if (purchaseState === 'onetime') {
      // Prefer consistent theme formatting if we can parse Recharge's formatted string.
      if (typeof rechargeFormatted?.unit === 'string') {
        if (priceMap?.money_format) {
          const cents = this.#parseFormattedMoneyToMinorUnits(rechargeFormatted.unit, currency);
          if (Number.isFinite(cents)) {
            applyFormattedPrice(cents);
            return;
          }
        }
        // Fallback: use Recharge's string as-is.
        applyFormattedString(rechargeFormatted.unit);
        return;
      }
      if (variantEntry) {
        const otpCents = Number(variantEntry.otp_price);
        if (Number.isFinite(otpCents)) applyFormattedPrice(otpCents);
      }
      return;
    }

    // If a selling plan id is selected and we have a mapped cents price, use it.
    if (sellingPlanId && variantEntry?.selling_plans?.[sellingPlanId]?.price != null) {
      const spCents = Number(variantEntry.selling_plans[sellingPlanId].price);
      if (Number.isFinite(spCents)) {
        applyFormattedPrice(spCents);
        return;
      }
    }

    // Fallback for Recharge v2: derive discounted price from its global config (strings like "£20.80").
    // This handles cases where no selling_plan input exists in the DOM.
    if (purchaseState === 'subscription') {
      // Prefer consistent theme formatting if we can parse Recharge's formatted string.
      if (typeof rechargeFormatted?.discounted === 'string') {
        if (priceMap?.money_format) {
          const cents = this.#parseFormattedMoneyToMinorUnits(rechargeFormatted.discounted, currency);
          if (Number.isFinite(cents)) {
            applyFormattedPrice(cents);
            return;
          }
        }
        // Fallback: use Recharge's string as-is.
        applyFormattedString(rechargeFormatted.discounted);
        return;
      }
    }

    // Default: OTP (Recharge string first, then cents map)
    if (typeof rechargeFormatted?.unit === 'string') {
      if (priceMap?.money_format) {
        const cents = this.#parseFormattedMoneyToMinorUnits(rechargeFormatted.unit, currency);
        if (Number.isFinite(cents)) {
          applyFormattedPrice(cents);
          return;
        }
      }
      applyFormattedString(rechargeFormatted.unit);
      return;
    }
    if (variantEntry) {
      const otpCents = Number(variantEntry.otp_price);
      if (Number.isFinite(otpCents)) {
        applyFormattedPrice(otpCents);
      }
    }
  }

  /**
   * Detects the currently selected purchase mode from the Recharge widget, if present.
   * @returns {'subscription' | 'onetime' | null}
   */
  #getRechargePurchaseState() {
    const rechargeRoots = this.#getRechargeSearchRoots();
    if (!rechargeRoots.length) return null;

    // 1) Prefer explicit purchase option radios inside the Recharge widget (most reliable).
    for (const root of rechargeRoots) {
      const purchaseOption = root.querySelector?.('input[type="radio"][name="purchaseOption"]:checked');
      if (purchaseOption instanceof HTMLInputElement) {
        const v = (purchaseOption.value || '').toLowerCase();
        if (v.includes('onetime') || v.includes('one') || v.includes('otp')) return 'onetime';
        if (v.includes('sub')) return 'subscription';
      }
    }

    // 2) Fallback: infer based on checked radio label text inside widget roots.
    const checkedRadios = rechargeRoots.flatMap((root) =>
      Array.from(root.querySelectorAll?.('input[type="radio"]:checked') || [])
    );
    for (const el of checkedRadios) {
      const labelText = el.closest('label')?.textContent?.toLowerCase() || '';
      const aria = el.getAttribute('aria-label')?.toLowerCase() || '';
      const combined = `${labelText} ${aria}`;
      if (combined.includes('one-time')) return 'onetime';
      if (combined.includes('subscribe')) return 'subscription';
    }

    // 3) Last resort: document-wide (can be wrong if multiple widgets exist).
    const docPurchaseOption = document.querySelector?.('input[type="radio"][name="purchaseOption"]:checked');
    if (docPurchaseOption instanceof HTMLInputElement) {
      const v = (docPurchaseOption.value || '').toLowerCase();
      if (v.includes('onetime') || v.includes('one') || v.includes('otp')) return 'onetime';
      if (v.includes('sub')) return 'subscription';
    }

    return null;
  }

  /**
   * Returns DOM roots to query for Recharge widget UI, including open shadow roots when available.
   * @returns {Array<Document | HTMLElement | ShadowRoot>}
   */
  #getRechargeSearchRoots() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return [];

    /** @type {Array<Document | HTMLElement | ShadowRoot>} */
    const roots = [];

    const productId = this.dataset.productId || '';

    let container = closestSection.querySelector?.('.recharge-subscription-widget') || null;
    let widget =
      (container instanceof HTMLElement && container.querySelector?.('recharge-subscription-widget')) ||
      closestSection.querySelector?.('recharge-subscription-widget') ||
      null;

    // If not found in the same section, fall back to a document-wide search and match by product-id.
    if (!(widget instanceof HTMLElement) && productId) {
      const widgets = Array.from(document.querySelectorAll('recharge-subscription-widget'));
      widget =
        widgets.find((el) => (el instanceof HTMLElement ? el.getAttribute('product-id') === productId : false)) || null;
      if (widget instanceof HTMLElement) {
        container = widget.closest('.recharge-subscription-widget') || widget.parentElement;
      }
    }

    if (container instanceof HTMLElement) roots.push(container);

    if (widget instanceof HTMLElement) {
      roots.push(widget);
      if (widget.shadowRoot) roots.push(widget.shadowRoot);
    }

    return roots;
  }

  /**
   * Parses a formatted money string (e.g. "£20.80" or "1,000") into minor units for the given currency.
   * @param {string} formatted
   * @param {string} currency
   * @returns {number | null}
   */
  #parseFormattedMoneyToMinorUnits(formatted, currency) {
    const upperCurrency = (currency || '').toUpperCase();
    const currencyDecimals = {
      BHD: 3,
      BIF: 0,
      BYR: 0,
      CLF: 4,
      CLP: 0,
      DJF: 0,
      GNF: 0,
      IQD: 3,
      ISK: 0,
      JOD: 3,
      JPY: 0,
      KMF: 0,
      KRW: 0,
      KWD: 3,
      LYD: 3,
      MRO: 5,
      OMR: 3,
      PYG: 0,
      RWF: 0,
      TND: 3,
      UGX: 0,
      UYI: 0,
      UYW: 4,
      VND: 0,
      VUV: 0,
      XAF: 0,
      XAG: 0,
      XAU: 0,
      XBA: 0,
      XBB: 0,
      XBC: 0,
      XBD: 0,
      XDR: 0,
      XOF: 0,
      XPD: 0,
      XPF: 0,
      XPT: 0,
      XSU: 0,
      XTS: 0,
      XUA: 0,
    };
    const precision = currencyDecimals[upperCurrency] ?? 2;

    // Extract digits + decimal separators, then normalize.
    const normalized = String(formatted || '').replace(/[^\d.,-]/g, '');
    if (!normalized) return null;

    // Handle "20.80" vs "20,80" style.
    let numStr = normalized;
    const hasComma = numStr.includes(',');
    const hasDot = numStr.includes('.');
    if (hasComma && hasDot) {
      // Assume the last separator is the decimal separator.
      if (numStr.lastIndexOf(',') > numStr.lastIndexOf('.')) {
        numStr = numStr.replace(/\./g, '').replace(',', '.');
      } else {
        numStr = numStr.replace(/,/g, '');
      }
    } else if (hasComma && !hasDot) {
      // If comma is used, decide whether it's a decimal separator or thousands separator.
      // Examples:
      // - "20,80" (decimal) -> "20.80" for precision 2
      // - "1,000" (thousands) -> "1000"
      const lastCommaIdx = numStr.lastIndexOf(',');
      const digitsAfterComma = numStr.slice(lastCommaIdx + 1).replace(/[^\d]/g, '').length;
      const commaCount = (numStr.match(/,/g) || []).length;

      if (commaCount === 1 && digitsAfterComma === precision && precision > 0) {
        // Single comma with expected decimal precision -> treat as decimal separator.
        numStr = numStr.replace(',', '.');
      } else {
        // Otherwise treat commas as thousands separators.
        numStr = numStr.replace(/,/g, '');
      }
    }

    const value = Number.parseFloat(numStr);
    if (!Number.isFinite(value)) return null;

    return Math.round(value * Math.pow(10, precision));
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
